<!-- Alert Container -->
<app-alert-container></app-alert-container>

<!-- Tournament Selection and Endpoint URL -->
<div class="row mb-3">
  <div class="col-lg-6">
    <label for="tournamentSelect" class="form-label">Tournament</label>
    <select 
      id="tournamentSelect"
      class="form-control"
      [disabled]="(operationState() !== 'idle' && operationState() !== 'completed') || loading()"
      (change)="handleTournamentChange($any($event.target).value)">
      <option value="">Select a tournament...</option>
      @for (tournament of availableTournaments(); track tournament.tournamentId) {
        <option [value]="tournament.tournamentId" [selected]="selectedTournament()?.tournamentId === tournament.tournamentId">
          {{ tournament.name }}
        </option>
      }
    </select>
  </div>
  <div class="col-lg-6">
    <label class="form-label">
      Endpoint URL
      <i class="fa fa-info-circle text-info info-icon" 
         [title]="'Current: ' + endpointUrl()"></i>
    </label>
    <div class="input-group">
      <input 
        type="text"
        class="form-control"
        placeholder="Override endpoint (optional)"
        [value]="endpointOverride()"
        [disabled]="operationState() !== 'idle' && operationState() !== 'completed'"
        (input)="updateEndpointUrl($any($event.target).value)" />
      @if (endpointOverride()) {
        <button 
          class="btn btn-outline-secondary"
          type="button"
          (click)="clearEndpointOverride()">
          <i class="fa fa-times"></i>
        </button>
      }
    </div>
    <small class="form-text text-muted">
      {{ endpointOverride() || endpointUrl() }}
    </small>
  </div>
</div>

<!-- Progress Section -->
@if (totalCount() > 0) {
  <div class="row mb-3">
    <div class="col-lg-12">
      <div class="progress-info mb-2">
        <strong>Progress:</strong>
        {{ checkedCount() }} of {{ totalCount() }} players checked ({{ progress() }}%)
      </div>
      <div class="progress mb-2">
        <div 
          class="progress-bar progress-bar-striped"
          [class.active]="operationState() === 'running'"
          [class.progress-bar-success]="operationState() === 'completed'"
          role="progressbar"
          [style.width.%]="progress()">
          {{ progress() }}%
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <div class="stats-label">
            <span class="text-success"><i class="fa fa-check-circle"></i> Passed:</span>
            <strong>{{ successCount() }}</strong>
          </div>
        </div>
        <div class="col-md-4">
          <div class="stats-label">
            <span class="text-danger"><i class="fa fa-times-circle"></i> Failed:</span>
            <strong>{{ failedCount() }}</strong>
          </div>
        </div>
        <div class="col-md-4">
          <div class="stats-label">
            <span class="text-muted"><i class="fa fa-circle-o"></i> Pending:</span>
            <strong>{{ totalCount() - checkedCount() }}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
}

<!-- Action Buttons -->
<div class="row mb-3">
  <div class="col-lg-12">
    <div class="btn-group" role="group">
      <button 
        class="btn btn-primary"
        [disabled]="!canRun()"
        (click)="startImageCheck()">
        <i class="fa fa-play"></i> Run Image Check
      </button>
      <button 
        class="btn btn-warning"
        [disabled]="!canPause()"
        (click)="pauseImageCheck()">
        <i class="fa fa-pause"></i> Pause
      </button>
      <button 
        class="btn btn-info"
        [disabled]="!canResume()"
        (click)="resumeImageCheck()">
        <i class="fa fa-play-circle"></i> Resume
      </button>
      <button 
        class="btn btn-danger"
        [disabled]="!canStop()"
        (click)="stopImageCheck()">
        <i class="fa fa-stop"></i> Stop
      </button>
      <button 
        class="btn btn-warning"
        [disabled]="!canRetryFailed()"
        (click)="retryFailed()">
        <i class="fa fa-refresh"></i> Retry Failed
      </button>
    </div>
    <button 
      class="btn btn-default ml-2"
      [disabled]="!canCopyResults()"
      (click)="copyResultsToClipboard()">
      <i class="fa fa-clipboard"></i> Copy Failed to Clipboard
    </button>
  </div>
</div>

<!-- Search and Filter -->
@if (totalCount() > 0) {
  <div class="row mb-3">
    <div class="col-md-4">
      <input 
        type="text"
        class="form-control"
        placeholder="Search players..."
        [value]="searchTerm()"
        (input)="searchTerm.set($any($event.target).value)" />
    </div>
    <div class="col-md-2">
      <select 
        class="form-control"
        [value]="pageSize()"
        (change)="onPageSizeChange($any($event.target).value)">
        <option value="5">5 rows</option>
        <option value="10">10 rows</option>
        <option value="20">20 rows</option>
        <option value="50">50 rows</option>
      </select>
    </div>
    <div class="col-md-6 text-right">
      <div class="checkbox checkbox-primary inline-checkbox">
        <input 
          type="checkbox" 
          id="showOnlyFailed" 
          [checked]="showOnlyFailed()"
          (change)="showOnlyFailed.set($any($event.target).checked); pageIndex.set(0)" />
        <label for="showOnlyFailed">
          Show only failed results ({{ failedCount() }})
        </label>
      </div>
    </div>
  </div>
}

<!-- Players Table -->
@if (totalCount() > 0) {
  <div class="table-responsive">
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <app-sortable-header
              [column]="'playerId'"
              [sortConfig]="{ key: sortColumn(), direction: sortDirection() }"
              (sort)="onSort($event)">
              LPGA ID
            </app-sortable-header>
          </th>
          <th>
            <app-sortable-header
              [column]="'firstName'"
              [sortConfig]="{ key: sortColumn(), direction: sortDirection() }"
              (sort)="onSort($event)">
              First Name
            </app-sortable-header>
          </th>
          <th>
            <app-sortable-header
              [column]="'lastName'"
              [sortConfig]="{ key: sortColumn(), direction: sortDirection() }"
              (sort)="onSort($event)">
              Last Name
            </app-sortable-header>
          </th>
          <th>
            <app-sortable-header
              [column]="'status'"
              [sortConfig]="{ key: sortColumn(), direction: sortDirection() }"
              (sort)="onSort($event)">
              Status
            </app-sortable-header>
          </th>
          <th>Code</th>
        </tr>
      </thead>
      <tbody>
        @for (player of paginatedPlayers(); track player.playerId) {
          <tr>
            <td>{{ player.playerId }}</td>
            <td>{{ player.firstName }}</td>
            <td>{{ player.lastName }}</td>
            <td [class]="getStatusColorClass(player.status)">
              <span [title]="player.errorMessage || ''">
                {{ getStatusIcon(player.status) }} {{ getStatusText(player) }}
              </span>
            </td>
            <td>{{ player.responseCode && player.responseCode !== 0 ? player.responseCode : '-' }}</td>
          </tr>
        } @empty {
          <tr>
            <td colspan="5" class="text-center text-muted">
              No players found
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <app-table-pagination
    [paginationData]="paginationResult()"
    [itemDescriptor]="'Players'"
    (pageChange)="onPageChange($event)">
  </app-table-pagination>
} @else if (!loading() && selectedTournament()) {
  <div class="alert alert-info">
    <i class="fa fa-info-circle"></i>
    No players found in the selected tournament.
  </div>
} @else if (!selectedTournament()) {
  <div class="alert alert-info">
    <i class="fa fa-info-circle"></i>
    Please select a tournament to begin.
  </div>
}

<!-- Loading Indicator -->
@if (loading()) {
  <div class="text-center">
    <div class="spinner-border" role="status">
      <span class="sr-only">Loading...</span>
    </div>
    <p class="mt-2">Loading...</p>
  </div>
}
